import { NavLink, useNavigate } from 'react-router-dom';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  LayoutDashboard,
  GraduationCap,
  UserCheck,
  Calendar,
  BookOpen,
  Bell,
  BarChart3,
  LogOut,
  School,
  X,
  MessageSquare,
  MessageCircle,
  ClipboardList,
  Settings,
  Video,
  Trophy,
  Building2,
  Users,
  Package,
  CreditCard,
  Lock,
  AlertTriangle,
  Clock,
  Mail,
  Activity,
  Wrench,
  Layers,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import type { UserDTO } from '@features/auth';
import type { UserRole } from '@/features/auth/domain/value-objects/UserRole';
import { useMobileMenu } from '@/shared/contexts/MobileMenuContext';
import { ThemeSwitch } from '@/shared/components/ThemeSwitch';
import { ActiveCycleIndicator } from './ActiveCycleIndicator';
import { useWorkspaceAccessStore } from '@/shared/stores/workspace-access.store';
import { useAppConfig } from '@/shared/hooks/useAppConfig';
import { useTheme } from '@/shared/providers/ThemeProvider';
import { useTerminology } from '@/shared/hooks/useTerminology';
import { useSidebarCollapse } from '@/shared/hooks/useSidebarCollapse';
import { Tooltip } from '@/shared/components/ui/tooltip';
import { useRankingStore } from '@/shared/stores/ranking.store';
import { useFeatureFlagsStore } from '@/shared/stores/feature-flags.store';
import { getLeagueTierConfig } from '@/features/ranking/domain/value-objects/LeagueTier';

interface MenuItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: string[];
}

/**
 * Get menu items based on user role.
 * Uses configurable terminology for all workspace-level labels.
 * Admin menu items use hardcoded labels (platform-level, not customizable).
 */
function getMenuItems(userRole: string, t: ReturnType<typeof useTerminology>): MenuItem[] {
  // Admin gets special minimal menu (platform management only — not customizable)
  if (userRole === 'admin') {
    return [
      { path: '/workspaces', label: 'Workspaces', icon: Building2, roles: ['admin'] },
      { path: '/clientes', label: 'Clientes', icon: Users, roles: ['admin'] },
      { path: '/admin/planes', label: 'Planes', icon: Package, roles: ['admin'] },
      { path: '/admin/pagos', label: 'Pagos', icon: CreditCard, roles: ['admin'] },
      // { path: '/admin/alertas', label: 'Alertas', icon: AlertTriangle, roles: ['admin'] }, // Moved inside /admin/pagos/alertas
      { path: '/admin/cron-jobs', label: 'Cron Jobs', icon: Clock, roles: ['admin'] },
      // { path: '/admin/versiones', label: 'Versiones', icon: History, roles: ['admin'] }, // Hidden - not ready for production
      { path: '/admin/emails', label: 'Emails', icon: Mail, roles: ['admin'] },
      { path: '/admin/pipeline-alerts', label: 'Pipeline Alerts', icon: Activity, roles: ['admin'] },
      { path: '/admin/video-analytics', label: 'Video Analytics', icon: BarChart3, roles: ['admin'] },
      { path: '/notificaciones', label: 'Notificaciones', icon: Bell, roles: ['admin'] },
      // { path: '/admin/app-config', label: 'Config App', icon: Palette, roles: ['admin'] }, // Hidden - not ready for production
      // { path: '/admin/logo-generator', label: 'Logo AI', icon: Sparkles, roles: ['admin'] }, // Hidden - not ready for production
      { path: '/admin/herramientas', label: 'Herramientas', icon: Wrench, roles: ['admin'] },
    ];
  }

  const baseItems: MenuItem[] = [
    { path: '/dashboard', label: t.navDashboard, icon: LayoutDashboard, roles: ['director', 'teacher', 'student'] },
    { path: '/estructura', label: t.navAcademicStructure, icon: School, roles: ['director'] },
    { path: '/gestion-videos', label: t.navVideoManagement, icon: Video, roles: ['director'] },
    { path: '/mis-materias', label: t.navMySubjects, icon: BookOpen, roles: ['director', 'teacher'] },
  ];

  // Role-specific enrollment/grades item
  if (userRole === 'director') {
    baseItems.push({
      path: '/calificaciones',
      label: t.navGradeManagement,
      icon: ClipboardList,
      roles: ['director'],
    });
  } else if (userRole === 'teacher') {
    baseItems.push({
      path: '/calificaciones',
      label: t.navGrades,
      icon: ClipboardList,
      roles: ['teacher'],
    });
  } else if (userRole === 'student') {
    baseItems.push(
      {
        path: '/inscripciones',
        label: t.navKardex,
        icon: UserCheck,
        roles: ['student'],
      }
    );
  }

  // Common items
  baseItems.push(
    { path: '/horarios', label: t.navSchedule, icon: Calendar, roles: ['director', 'teacher', 'student'] },
    { path: '/lms', label: t.navLMS, icon: GraduationCap, roles: ['director', 'teacher', 'student'] },
    { path: '/flashcards', label: 'Flashcards', icon: Layers, roles: ['director', 'teacher', 'student'] },
    { path: '/ranking', label: 'Clasificación', icon: Trophy, roles: ['director', 'teacher', 'student'] },
    { path: '/mensajes', label: 'Mensajes', icon: MessageCircle, roles: ['director', 'teacher', 'student'] },
    { path: '/forum', label: t.navForum, icon: MessageSquare, roles: ['director', 'teacher', 'student'] },
    { path: '/notificaciones', label: t.navNotifications, icon: Bell, roles: ['director', 'teacher', 'student'] },
    { path: '/estadisticas', label: t.navStatistics, icon: BarChart3, roles: ['director'] },
    { path: '/configuracion', label: t.navSettings, icon: Settings, roles: ['director'] },
  );

  return baseItems;
}

interface SidebarProps {
  user: UserDTO;
  onLogout: () => void;
}

export const Sidebar = memo(function Sidebar({ user, onLogout }: SidebarProps) {
  const { isOpen, close } = useMobileMenu();
  const navigate = useNavigate();
  const { isAccessBlocked } = useWorkspaceAccessStore();
  const { appName, logoSmall, logoDarkSmall } = useAppConfig();
  const { resolvedTheme } = useTheme();
  const terminology = useTerminology();
  const { isCollapsed, toggle } = useSidebarCollapse();
  const { currentRanking } = useRankingStore();
  const { isEnabled } = useFeatureFlagsStore();
  const tierConfig = currentRanking ? getLeagueTierConfig(currentRanking.tier) : null;
  const [isHoveringEdge, setIsHoveringEdge] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // Update isDesktop on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use dark logo in dark mode if available, otherwise fall back to light logo
  const currentLogo = resolvedTheme === 'dark' && logoDarkSmall ? logoDarkSmall : logoSmall;

  const flashcardsSidebarEnabled = isEnabled('flashcards_sidebar');
  const filteredItems = useMemo(() => {
    const menuItems = getMenuItems(user.role || 'student', terminology);
    return menuItems
      .filter(item => user.role && item.roles.includes(user.role))
      .filter(item => item.path !== '/flashcards' || flashcardsSidebarEnabled);
  }, [user.role, terminology, flashcardsSidebarEnabled]);

  // Modules that remain accessible when access is blocked
  const allowedWhenBlocked = ['/configuracion', '/perfil'];

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, close]);

  // Keyboard shortcut: Cmd/Ctrl + B to toggle sidebar (desktop only)
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      // Only on desktop (width >= 1024px)
      if (window.innerWidth < 1024) return;

      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        toggle();
      }
    };

    document.addEventListener('keydown', handleKeyboard);
    return () => document.removeEventListener('keydown', handleKeyboard);
  }, [toggle]);

  // Double-click on logo to toggle (desktop only)
  const handleLogoDoubleClick = useCallback(() => {
    if (window.innerWidth < 1024) return;
    toggle();
  }, [toggle]);

  // Track logo clicks for double-click detection
  useEffect(() => {
    if (logoClickCount === 2) {
      handleLogoDoubleClick();
      setLogoClickCount(0);
    }

    const timer = setTimeout(() => setLogoClickCount(0), 300);
    return () => clearTimeout(timer);
  }, [logoClickCount, handleLogoDoubleClick]);

  return (
    <>
      {/* Backdrop overlay for mobile */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-in fade-in duration-200"
          onClick={close}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        id="mobile-sidebar"
        className={`
          h-screen bg-white dark:bg-gray-900 shadow-lg flex flex-col
          transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          fixed left-0 top-0 z-40 lg:sticky lg:top-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{
          width: isDesktop ? (isCollapsed ? '80px' : '256px') : '256px',
        }}
      >
        {/* Hover-trigger edge (desktop only) */}
        <div
          className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 h-32 w-6 cursor-pointer z-10"
          onMouseEnter={() => setIsHoveringEdge(true)}
          onMouseLeave={() => setIsHoveringEdge(false)}
        >
          {/* Visual indicator on hover */}
          {isHoveringEdge && (
            <div
              onClick={toggle}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-6 h-16 rounded-r-xl bg-gradient-to-r from-[var(--brand-primary)]/10 to-[var(--brand-primary)]/20 dark:from-[var(--brand-primary)]/20 dark:to-[var(--brand-primary)]/30 border border-l-0 border-[var(--brand-primary)]/30 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:border-[var(--brand-primary)]/50 transition-all duration-200 shadow-lg animate-in slide-in-from-left-2 fade-in duration-200"
            >
              <div className="w-1 h-8 bg-[var(--brand-primary)]/50 rounded-full animate-pulse" />
            </div>
          )}
        </div>

        {/* Logo */}
        <div className={`p-6 border-b border-gray-100 dark:border-gray-800 transition-all duration-300 ${isCollapsed ? 'lg:px-4' : ''}`}>
          <div className="flex items-center justify-between">
            <div
              className={`flex items-center gap-3 cursor-pointer select-none ${isCollapsed ? 'lg:justify-center lg:w-full' : ''}`}
              onClick={() => setLogoClickCount(prev => prev + 1)}
              title="Doble click para colapsar/expandir"
            >
              <Tooltip content={`${appName} (Doble click para ${isCollapsed ? 'expandir' : 'colapsar'})`} side="right">
                <div className="w-10 h-10 rounded-xl bg-[var(--brand-primary)] flex items-center justify-center overflow-hidden flex-shrink-0 hover:scale-105 active:scale-95 transition-transform duration-200">
                  {currentLogo ? (
                    <img src={currentLogo} alt={appName} className="w-full h-full object-contain" />
                  ) : (
                    <GraduationCap className="w-6 h-6 text-white" />
                  )}
                </div>
              </Tooltip>
              {!isCollapsed && (
                <div className="lg:block hidden overflow-hidden animate-in fade-in slide-in-from-left-2 duration-200">
                  <div className="font-semibold text-gray-900 dark:text-gray-100">{appName}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{terminology.getRoleLabel(user.role as UserRole)}</div>
                </div>
              )}
              {/* Always show on mobile */}
              <div className="lg:hidden block">
                <div className="font-semibold text-gray-900 dark:text-gray-100">{appName}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{terminology.getRoleLabel(user.role as UserRole)}</div>
              </div>
            </div>
            {/* Close button for mobile */}
            <button
              onClick={close}
              className="lg:hidden p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2"
              aria-label="Close menu"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Toggle button - Desktop only - Premium design */}
          <div className="hidden lg:block relative mt-4">
            <button
              onClick={toggle}
              className={`
                w-full flex items-center justify-center py-2.5 rounded-xl
                bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700
                hover:from-[var(--brand-primary)]/10 hover:to-[var(--brand-primary)]/5
                dark:hover:from-[var(--brand-primary)]/20 dark:hover:to-[var(--brand-primary)]/10
                border border-gray-200 dark:border-gray-700 hover:border-[var(--brand-primary)]/30
                text-gray-600 dark:text-gray-400 hover:text-[var(--brand-primary)]
                transition-all duration-200 group relative overflow-hidden
                focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2
                hover:scale-[1.02] active:scale-[0.98]
              `}
              aria-label={isCollapsed ? 'Expandir sidebar (Cmd+B)' : 'Comprimir sidebar (Cmd+B)'}
              title={isCollapsed ? 'Expandir sidebar (⌘B)' : 'Comprimir sidebar (⌘B)'}
            >
              {/* Background shine effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

              {isCollapsed ? (
                <div className="flex items-center animate-in zoom-in-75 duration-200">
                  <PanelLeftOpen className="w-4 h-4" />
                </div>
              ) : (
                <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-1 duration-200">
                  <PanelLeftClose className="w-4 h-4" />
                  <span className="text-xs font-medium">Comprimir</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200/50 dark:bg-gray-600/50 text-gray-500 dark:text-gray-400 font-mono">⌘B</span>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Access Blocked Warning Banner */}
        {isAccessBlocked && user.role !== 'admin' && (
          <div className={`mx-4 mt-4 animate-in fade-in slide-in-from-top-2 duration-300 ${isCollapsed ? 'lg:mx-2' : ''}`}>
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                {!isCollapsed && (
                  <div className="lg:block hidden animate-in fade-in duration-200">
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                      Acceso limitado
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                      Tu suscripción ha vencido. Ve a Configuración para renovar.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Active Academic Cycle Indicator - Hidden for ADMIN users */}
        {user.role !== 'admin' && <ActiveCycleIndicator variant="sidebar" isCollapsed={isCollapsed} />}

        {/* Navigation */}
        <nav id="main-navigation" aria-label="Navegación principal" className="flex-1 p-4 overflow-y-auto custom-scrollbar">
          <div className="space-y-1">
            {filteredItems.map((item) => {
              const Icon = item.icon;

              // Generate data-onboarding ID for tour steps
              const onboardingId = `sidebar-${item.path.replace('/', '')}-link`;

              // Check if this module should be blocked
              const isModuleBlocked = isAccessBlocked &&
                user.role !== 'admin' &&
                !allowedWhenBlocked.includes(item.path);

              if (isModuleBlocked) {
                // Render disabled link for blocked modules
                const blockedContent = (
                  <div
                    key={item.path}
                    id={onboardingId}
                    data-onboarding={onboardingId}
                    className={`w-full flex items-center gap-3 rounded-xl text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60 transition-all ${
                      isCollapsed ? 'lg:justify-center lg:px-3 py-3' : 'px-4 py-3'
                    }`}
                    title="Módulo no disponible - Renueva tu suscripción"
                  >
                    <Lock className="w-5 h-5 flex-shrink-0" />
                    {!isCollapsed && <span className="text-sm lg:block hidden">{item.label}</span>}
                    {/* Always show on mobile */}
                    <span className="text-sm lg:hidden block">{item.label}</span>
                  </div>
                );

                return isCollapsed ? (
                  <Tooltip key={item.path} content={`${item.label} (Bloqueado)`} side="right">
                    {blockedContent}
                  </Tooltip>
                ) : (
                  blockedContent
                );
              }

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => close()}
                  id={onboardingId}
                  data-onboarding={onboardingId}
                  className={({ isActive }) =>
                    `w-full flex items-center gap-3 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2 group relative overflow-hidden ${
                      isCollapsed ? 'lg:justify-center lg:px-3 py-3' : 'px-4 py-3'
                    } ${
                      isActive
                        ? 'bg-[var(--brand-primary)] text-white shadow-lg shadow-purple-200 dark:shadow-purple-900/30'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:translate-x-1'
                    }`
                  }
                >
                  {({ isActive }) => {
                    const content = (
                      <>
                        <Icon className={`w-5 h-5 flex-shrink-0 transition-all duration-200 ${!isActive && 'group-hover:scale-110 group-hover:rotate-3'}`} />
                        {!isCollapsed && (
                          <span className="text-sm lg:block hidden transition-all duration-200">{item.label}</span>
                        )}
                        {/* Always show on mobile */}
                        <span className="text-sm lg:hidden block">{item.label}</span>
                        {/* Active indicator */}
                        {isActive && !isCollapsed && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                        )}
                      </>
                    );

                    return isCollapsed ? (
                      <Tooltip content={item.label} side="right">
                        <div className="w-full flex items-center justify-center">
                          {content}
                        </div>
                      </Tooltip>
                    ) : (
                      content
                    );
                  }}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-800" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}>
          {/* Theme Toggle */}
          <div className="mb-3">
            <ThemeSwitch compact={isCollapsed} />
          </div>

          {/* User info - clickable to go to profile */}
          <button
            onClick={() => {
              close();
              navigate('/perfil');
            }}
            className={`w-full flex items-center gap-3 mb-3 p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 group hover:scale-[1.02] active:scale-[0.98] ${
              isCollapsed ? 'lg:justify-center lg:px-0' : ''
            }`}
          >
            <Tooltip content={`${user.name} - Ver perfil`} side="right" disabled={!isCollapsed}>
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center hover:scale-105 transition-transform duration-200 overflow-hidden">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-semibold text-sm">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                {/* League Badge */}
                {tierConfig && user.role === 'student' && (
                  <div
                    className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-white dark:bg-gray-800 border-2 border-white dark:border-gray-800 shadow-md flex items-center justify-center"
                    style={{ boxShadow: `0 2px 6px ${tierConfig.color}40` }}
                    title={`Liga ${tierConfig.name}`}
                  >
                    <span className="text-[10px]">{tierConfig.icon}</span>
                  </div>
                )}
              </div>
            </Tooltip>
            {!isCollapsed && (
              <div className="flex-1 min-w-0 text-left overflow-hidden lg:block hidden animate-in fade-in duration-200">
                <div className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{user.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
              </div>
            )}
            {/* Always show on mobile */}
            <div className="flex-1 min-w-0 text-left lg:hidden block">
              <div className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{user.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
            </div>
            {!isCollapsed && (
              <Settings className="w-4 h-4 text-gray-400 group-hover:text-[var(--brand-primary)] transition-colors flex-shrink-0 lg:block hidden" />
            )}
            <Settings className="w-4 h-4 text-gray-400 group-hover:text-[var(--brand-primary)] transition-colors flex-shrink-0 lg:hidden block" />
          </button>

          <button
            onClick={onLogout}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 text-gray-600 dark:text-gray-300 hover:from-red-50 hover:to-red-100 dark:hover:from-red-900/20 dark:hover:to-red-800/20 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2 border border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-700 hover:scale-[1.02] active:scale-[0.98] ${
              isCollapsed ? 'lg:px-2' : ''
            }`}
          >
            <LogOut className="w-4 h-4" />
            {!isCollapsed && (
              <span className="text-sm overflow-hidden whitespace-nowrap lg:block hidden">Cerrar sesión</span>
            )}
            {/* Always show on mobile */}
            <span className="text-sm lg:hidden block">Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Custom scrollbar styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 3px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.2);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>
    </>
  );
});
